import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="events-table-sort"
export default class extends Controller {
  static targets = ["header", "indicator", "content"]
  
  connect() {
    this.currentColumn = null
    this.ascending = true
  }
  
  toggle(event) {
    const header = event.currentTarget
    const column = header.dataset.column
    
    // Update sort indicators
    this.headerTargets.forEach(h => {
      const indicator = h.querySelector('[data-events-table-sort-target="indicator"]')
      if (h === header) {
        // If clicking the same column, toggle direction
        if (this.currentColumn === column) {
          this.ascending = !this.ascending
        } else {
          this.ascending = true
          this.currentColumn = column
        }
        
        indicator.textContent = this.ascending ? "↑" : "↓"
        indicator.classList.remove("text-gray-400")
        indicator.classList.add("text-blue-500")
      } else {
        // Reset other columns
        indicator.textContent = "⇅"
        indicator.classList.remove("text-blue-500")
        indicator.classList.add("text-gray-400")
      }
    })
    
    // Sort the table
    this.sortTable(column, this.ascending)
  }
  
  sortTable(column, ascending) {
    const rows = Array.from(this.contentTarget.querySelectorAll("tr"))
    
    // Sort the rows
    rows.sort((rowA, rowB) => {
      // Find the index of the column to sort by
      const columnIndex = this.headerTargets.findIndex(h => h.dataset.column === column)
      if (columnIndex === -1) return 0
      
      // Get the cell values
      const cellA = rowA.querySelectorAll("td")[columnIndex]
      const cellB = rowB.querySelectorAll("td")[columnIndex]
      
      if (!cellA || !cellB) return 0
      
      let valueA = cellA.textContent.trim()
      let valueB = cellB.textContent.trim()
      
      // Handle date comparisons
      if (column === "date_created" || column === "date_completed" || column === "last_updated") {
        // For "X ago" format in last_updated
        if (column === "last_updated") {
          valueA = this.convertTimeAgoToDate(valueA)
          valueB = this.convertTimeAgoToDate(valueB)
        } else {
          // For standard date format
          valueA = new Date(valueA)
          valueB = new Date(valueB)
        }
        
        // Handle empty dates
        if (valueA == "Invalid Date") valueA = new Date(0)
        if (valueB == "Invalid Date") valueB = new Date(0)
        
        return ascending ? valueA - valueB : valueB - valueA
      }
      
      // Regular string comparison
      return ascending 
        ? valueA.localeCompare(valueB) 
        : valueB.localeCompare(valueA)
    })
    
    // Reorder the rows in the DOM
    rows.forEach(row => {
      this.contentTarget.appendChild(row)
    })
  }
  
  // Helper to convert "X days/hours/etc ago" to a Date object
  convertTimeAgoToDate(timeAgo) {
    const now = new Date()
    const parts = timeAgo.split(' ')
    
    if (parts.length < 3) return now
    
    const amount = parseInt(parts[0])
    const unit = parts[1]
    
    if (isNaN(amount)) return now
    
    let milliseconds = 0
    
    switch(unit) {
      case 'seconds':
      case 'second':
        milliseconds = amount * 1000
        break
      case 'minutes':
      case 'minute':
        milliseconds = amount * 60 * 1000
        break
      case 'hours':
      case 'hour':
        milliseconds = amount * 60 * 60 * 1000
        break
      case 'days':
      case 'day':
        milliseconds = amount * 24 * 60 * 60 * 1000
        break
      case 'weeks':
      case 'week':
        milliseconds = amount * 7 * 24 * 60 * 60 * 1000
        break
      case 'months':
      case 'month':
        milliseconds = amount * 30 * 24 * 60 * 60 * 1000
        break
      case 'years':
      case 'year':
        milliseconds = amount * 365 * 24 * 60 * 60 * 1000
        break
    }
    
    return new Date(now - milliseconds)
  }
}
